// ** Modules
import React from 'react';
import { useId } from 'react';

// ** UI
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Divider from '@mui/material/Divider';
import Collapse from '@mui/material/Collapse';
import ButtonBase from '@mui/material/ButtonBase';
import Typography from '@mui/material/Typography';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import useMediaQuery from '@mui/material/useMediaQuery';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import MailOutlinedIcon from '@mui/icons-material/MailOutlined';
import PhoneOutlinedIcon from '@mui/icons-material/PhoneOutlined';
import { alpha, useTheme } from '@mui/material/styles';

// ** Components
import StrapiImageRenderer from '@/components/strapi/StrapiImageRenderer';

// ** Types
import type { UploadFile } from '@/types/graphql';
import type { AppContext } from '@/types/contextTypes';

/** View-model for one contact card — pass `host` for Strapi-hosted images, omit for internal `/public` paths. */
export type PersonCardModel = {
	/** Stable id for React keys (e.g. Strapi `documentId`). */
	id: string;
	/** Display name (combine CMS first + last name when mapping). */
	name: string;
	/** Role subtitles; one entry per line (e.g. `positionLine1`…`3`). */
	roleLines: string[];
	/** Image source URL — relative `/public` path or absolute Strapi URL. */
	imageSrc?: string;
	imageAlt?: string;
	/** Required when `imageSrc` is a Strapi-hosted URL. */
	host?: AppContext['host'];
	/** Shown only when set; add a Strapi field if editors must supply it. */
	linkedinUrl?: string;
	phone?: string;
	email?: string;
	/**
	 * Filter discriminant only — **not rendered**. Must match one of your filter `SearchOption.value`
	 * strings (except the "all" option); map from CMS `team` JSON or a dedicated enum/slug field.
	 */
	team: string;
	imageData: UploadFile;
	position?: string;
};

export type PersonCardProps = PersonCardModel;

type ContactRow = {
	key: 'linkedin' | 'phone' | 'email';
	value: string;
	href: string;
	label: string;
	target?: '_blank';
	rel?: string;
};

const BASE_TEXT_SX = {
	color: 'common.black',
	fontSize: '1rem',
	lineHeight: '1.375rem',
	letterSpacing: '-0.008px'
} as const;

const NAME_TEXT_SX = {
	...BASE_TEXT_SX,
	fontWeight: 500,
	'@media (min-width:900px)': {
		fontSize: '1.125rem',
		lineHeight: '1.5rem',
		letterSpacing: '0.05px'
	}
} as const;

const BODY_TEXT_SX = {
	...BASE_TEXT_SX,
	fontWeight: 400,
	'@media (min-width:900px)': {
		fontSize: '0.875rem',
		lineHeight: '1.375rem',
		letterSpacing: '0.007px'
	}
} as const;

const getPhoneHref = (value: string) => `tel:${value.replace(/\s+/g, '')}`;

const getContactRows = ({
	linkedinUrl,
	phone,
	email
}: Pick<PersonCardProps, 'linkedinUrl' | 'phone' | 'email'>): ContactRow[] => {
	const rows: ContactRow[] = [];
	if (linkedinUrl) {
		rows.push({
			key: 'linkedin',
			value: 'LinkedIn',
			href: linkedinUrl,
			label: 'LinkedIn'
		});
	}
	if (phone) {
		rows.push({
			key: 'phone',
			value: phone,
			href: getPhoneHref(phone),
			label: `Telefon: ${phone}`
		});
	}
	if (email) {
		rows.push({
			key: 'email',
			value: email,
			href: `mailto:${email}`,
			label: `E-Mail: ${email}`,
			target: '_blank',
			rel: 'noopener noreferrer'
		});
	}
	return rows;
};

const PersonCard = ({
	name,
	roleLines,
	imageSrc,
	imageAlt = '',
	host,
	linkedinUrl,
	phone,
	email,
	imageData
}: PersonCardProps) => {
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
	const panelId = useId();
	const headingId = useId();
	const [isMobileExpanded, setIsMobileExpanded] = React.useState(false);
	const contactRows = React.useMemo(() => getContactRows({ linkedinUrl, phone, email }), [linkedinUrl, phone, email]);

	const contactLinkSx = {
		display: 'inline-flex',
		alignItems: 'center',
		gap: { xs: 2, md: 1.5 },
		color: 'common.black',
		textDecoration: 'none',
		transition: 'opacity 200ms ease, color 200ms ease',
		'& .contact-icon': {
			transition: 'transform 200ms ease'
		},
		'& .contact-text': {
			position: 'relative',
			'&::after': {
				content: '""',
				position: 'absolute',
				bottom: -1,
				left: 0,
				width: '100%',
				height: '1px',
				backgroundColor: 'currentColor',
				transform: 'scaleX(0)',
				transformOrigin: 'left',
				transition: 'transform 220ms cubic-bezier(0.22, 1, 0.36, 1)'
			}
		},
		'&:hover': {
			opacity: 0.7,
			'& .contact-icon': {
				transform: 'scale(1.15)'
			},
			'& .linkedin-icon': {
				color: '#0A66C2'
			},
			'& .contact-text::after': {
				transform: 'scaleX(1)'
			}
		},
		'&:focusVisible': {
			outline: `2px solid ${alpha(theme.palette.common.black, 0.35)}`,
			outlineOffset: 2,
			borderRadius: 1
		}
	};

	if (isMobile) {
		return (
			<Box
				component="li"
				sx={{
					listStyle: 'none',
					width: '100%',
					justifySelf: 'stretch'
				}}
			>
				<ButtonBase
					onClick={() => setIsMobileExpanded((prev) => !prev)}
					aria-expanded={isMobileExpanded}
					aria-controls={panelId}
					disableRipple
					sx={{
						width: '100%',
						display: 'flex',
						alignItems: 'flex-start',
						justifyContent: 'space-between',
						textAlign: 'left',
						px: 0,
						pt: 2,
						pb: 4,
						backgroundColor: 'transparent',
						color: 'common.black',
						WebkitTapHighlightColor: 'transparent',
						'&:hover, &:active': {
							backgroundColor: 'transparent',
							color: 'common.black'
						},
						'&:focusVisible': {
							outline: `2px solid ${alpha(theme.palette.common.black, 0.35)}`,
							outlineOffset: 2,
							borderRadius: 1
						},
						'& .MuiTouchRipple-root': { display: 'none' }
					}}
				>
					<Box sx={{ pr: 2, minWidth: 0 }}>
						<Typography variant="body-md" component="h3" id={headingId} sx={NAME_TEXT_SX}>
							{name}
						</Typography>
						{roleLines.length ? (
							<Box sx={{ mt: 2 }}>
								{roleLines.map((line) => (
									<Typography key={line} variant="body-sm" component="p" sx={{ ...BODY_TEXT_SX, m: 0 }}>
										{line}
									</Typography>
								))}
							</Box>
						) : null}
					</Box>

					<ExpandMoreIcon
						aria-hidden="true"
						sx={{
							color: 'common.black',
							mt: 0.5,
							transform: isMobileExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
							transition: 'transform 300ms cubic-bezier(0.22, 1, 0.36, 1)'
						}}
					/>
				</ButtonBase>

				<Collapse
					in={isMobileExpanded}
					timeout={320}
					easing={{
						enter: 'cubic-bezier(0.22, 1, 0.36, 1)',
						exit: 'cubic-bezier(0.22, 1, 0.36, 1)'
					}}
				>
					<Box id={panelId} role="region" aria-labelledby={headingId} sx={{ pt: 6, pb: 3 }}>
						<Box
							sx={{
								position: 'relative',
								width: '100%',
								aspectRatio: '276 / 165',
								borderRadius: 2,
								overflow: 'hidden'
							}}
						>
							<StrapiImageRenderer
								src={imageSrc}
								host={host}
								alt={imageAlt}
								fill
								imageData={imageData}
								loading="eager"
								sizes="(max-width: 599px) 100vw, 276px"
								style={{ objectFit: 'cover', objectPosition: 'center' }}
							/>
						</Box>

						{contactRows.length ? (
							<Box
								component="ul"
								aria-label={`${name} Kontakt`}
								sx={{
									listStyle: 'none',
									m: 0,
									p: 0,
									mt: 4
								}}
							>
								{contactRows.map((row, index) => {
									const prevKey = index > 0 ? contactRows[index - 1].key : null;
									const mt = prevKey === 'linkedin' ? 6 : prevKey === 'phone' ? 4 : 0;
									return (
										<Box key={row.key} component="li" sx={{ mt }}>
											{row.key === 'linkedin' ? (
												<Box
													component="a"
													href={row.href}
													target={row.target}
													rel={row.rel}
													aria-label={`${name}: ${row.label}`}
													sx={contactLinkSx}
												>
													<LinkedInIcon
														aria-hidden="true"
														className="contact-icon linkedin-icon"
														sx={{ fontSize: '2rem' }}
													/>
												</Box>
											) : (
												<Box
													component="a"
													href={row.href}
													target={row.target}
													rel={row.rel}
													aria-label={`${name}: ${row.label}`}
													sx={contactLinkSx}
												>
													{row.key === 'phone' ? (
														<PhoneOutlinedIcon aria-hidden="true" className="contact-icon" sx={{ fontSize: '1rem' }} />
													) : (
														<MailOutlinedIcon aria-hidden="true" className="contact-icon" sx={{ fontSize: '1rem' }} />
													)}
													<Typography variant="body-sm" component="span" className="contact-text" sx={BODY_TEXT_SX}>
														{row.value}
													</Typography>
												</Box>
											)}
										</Box>
									);
								})}
							</Box>
						) : null}
					</Box>
				</Collapse>

				<Divider
					sx={{
						borderColor: alpha(theme.palette.common.black, 0.18)
					}}
				/>
			</Box>
		);
	}

	return (
		<Card
			component="li"
			aria-labelledby={headingId}
			sx={{
				listStyle: 'none',
				width: '100%',
				maxWidth: 308,
				height: 428,
				borderRadius: 2,
				backgroundColor: theme.palette.heroLightBackground.main,
				boxShadow: 'none',
				overflow: 'hidden',
				justifySelf: 'center',
				display: 'flex',
				flexDirection: 'column',
				'&:focus-within': {
					outline: `2px solid ${alpha(theme.palette.common.black, 0.35)}`,
					outlineOffset: '2px'
				}
			}}
		>
			<Box
				sx={{
					position: 'relative',
					width: '100%',
					aspectRatio: '276 / 165',
					flexShrink: 0
				}}
			>
				<StrapiImageRenderer
					src={imageSrc}
					host={host}
					alt={imageAlt}
					fill
					imageData={imageData}
					sizes="(max-width: 1439px) 33vw, 308px"
					style={{ objectFit: 'cover', objectPosition: 'center' }}
				/>
			</Box>

			<Box
				sx={{
					display: 'flex',
					flexDirection: 'column',
					flexGrow: 1,
					pl: 8,
					pr: 2,
					pt: 6,
					pb: 4
				}}
			>
				<Box>
					<Typography variant="body-md" component="h3" id={headingId} sx={NAME_TEXT_SX}>
						{name}
					</Typography>
					{roleLines.length ? (
						<Box sx={{ mt: 2 }}>
							{roleLines.map((line) => (
								<Typography key={line} variant="body-sm" component="p" sx={{ ...BODY_TEXT_SX, m: 0 }}>
									{line}
								</Typography>
							))}
						</Box>
					) : null}
				</Box>

				{contactRows.length ? (
					<Box
						component="ul"
						aria-label={`${name} Kontakt`}
						sx={{
							display: 'flex',
							flexDirection: 'column',
							gap: 1,
							listStyle: 'none',
							m: 0,
							p: 0,
							mt: 'auto',
							mb: 3,
							pt: 10
						}}
					>
						{contactRows.map((row) => (
							<Box key={row.key} component="li">
								{row.key === 'linkedin' ? (
									<Box
										component="a"
										href={row.href}
										target={row.target}
										rel={row.rel}
										aria-label={`${name}: ${row.label}`}
										sx={contactLinkSx}
									>
										<LinkedInIcon aria-hidden="true" className="contact-icon linkedin-icon" sx={{ fontSize: '1rem' }} />
									</Box>
								) : (
									<Box
										component="a"
										href={row.href}
										target={row.target}
										rel={row.rel}
										aria-label={`${name}: ${row.label}`}
										sx={contactLinkSx}
									>
										{row.key === 'phone' ? (
											<PhoneOutlinedIcon aria-hidden="true" className="contact-icon" sx={{ fontSize: '1rem' }} />
										) : (
											<MailOutlinedIcon aria-hidden="true" className="contact-icon" sx={{ fontSize: '1rem' }} />
										)}
										<Typography variant="body-sm" component="span" className="contact-text" sx={BODY_TEXT_SX}>
											{row.value}
										</Typography>
									</Box>
								)}
							</Box>
						))}
					</Box>
				) : null}
			</Box>
		</Card>
	);
};

export default PersonCard;
